import React, { useMemo, useState } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { CheckCircle2, ArrowLeft, ShieldCheck, Lock, CreditCard, Smartphone, Building2 } from 'lucide-react';
import Navbar from '../../components/Common/Navbar';
import Footer from '../../components/Common/Footer';
import { theme } from '../../styles/theme';

const c = theme.colors;

const PLANS = {
  pro: {
    name: 'Pro',
    amount: 999,
    tagline: 'For merchants getting started',
    features: [
      'Hosted checkout page',
      'Basic merchant dashboard',
      'Email support',
    ],
  },
  premium: {
    name: 'Premium',
    amount: 1499,
    tagline: 'For growing partners scaling volume',
    features: [
      'Everything in Pro',
      'API-based tools for partners',
      'Higher transaction limits',
      'Priority support',
    ],
  },
  platinum: {
    name: 'Platinum',
    amount: 1999,
    tagline: 'For high-volume merchants',
    features: [
      'Everything in Premium',
      'Dedicated relationship manager',
      'Custom integration support',
      'Top-tier transaction limits',
    ],
  },
};

const Checkout = () => {
  const { plan: planSlug } = useParams();
  const navigate = useNavigate();
  const plan = PLANS[planSlug] || PLANS.pro;

  const [method, setMethod] = useState('upi');
  const [form, setForm] = useState({
    name: '', email: '', phone: '', businessName: '', gstin: '',
    upiId: '', cardNumber: '', cardName: '', cardExpiry: '', cardCvv: '',
    bank: 'HDFC',
  });
  const [submitting, setSubmitting] = useState(false);

  const gst = useMemo(() => Math.round(plan.amount * 0.18), [plan.amount]);
  const total = useMemo(() => plan.amount + gst, [plan.amount, gst]);

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const onSubmit = (e) => {
    e.preventDefault();
    if (!form.name || !form.email || !form.phone) {
      alert('Naam, email aur phone bharein.');
      return;
    }
    setSubmitting(true);
    // Demo flow — no real backend call. Simulate processing then route to success page.
    setTimeout(() => {
      const orderRef = 'A1X' + Date.now().toString().slice(-10);
      navigate(`/checkout/${planSlug}/success?ref=${orderRef}&amt=${total}`);
    }, 1600);
  };

  const styles = {
    page: { fontFamily: theme.font.family, color: c.body, backgroundColor: c.surfaceAlt, minHeight: '100vh' },
    hero: {
      background: theme.gradient.dark,
      color: '#fff', padding: '48px 24px 56px',
    },
    heroInner: { maxWidth: theme.maxWidth, margin: '0 auto' },
    back: { display: 'inline-flex', alignItems: 'center', gap: '6px', color: 'rgba(255,255,255,0.78)', textDecoration: 'none', fontSize: '0.875rem', marginBottom: '16px' },
    h1: { fontSize: 'clamp(1.6rem, 3vw, 2.25rem)', fontWeight: 800, letterSpacing: '-0.02em', margin: '0 0 8px' },
    sub: { color: 'rgba(255,255,255,0.7)', fontSize: '1rem' },

    body: { maxWidth: theme.maxWidth, margin: '0 auto', padding: '40px 24px 80px', display: 'grid', gridTemplateColumns: '1.4fr 1fr', gap: '28px' },
    bodyMobile: { gridTemplateColumns: '1fr' },

    card: {
      background: c.surface, border: `1px solid ${c.border}`,
      borderRadius: theme.radius.lg, padding: '28px', boxShadow: theme.shadow.sm,
    },
    sectionH: { fontSize: '0.95rem', fontWeight: 700, color: c.ink, letterSpacing: '0.02em', marginBottom: '14px', display: 'flex', alignItems: 'center', gap: '8px' },
    label: { fontSize: '0.78rem', fontWeight: 600, color: c.muted, textTransform: 'uppercase', letterSpacing: '0.06em', marginBottom: '6px', display: 'block' },
    input: {
      width: '100%', padding: '12px 14px', fontSize: '0.9375rem',
      border: `1px solid ${c.border}`, borderRadius: theme.radius.sm,
      fontFamily: 'inherit', outline: 'none',
      transition: 'border-color .15s ease, box-shadow .15s ease',
    },
    row: { display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px', marginBottom: '14px' },

    methodRow: { display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: '10px', marginBottom: '20px' },
    methodTab: (active) => ({
      padding: '14px 8px', textAlign: 'center', cursor: 'pointer',
      border: `1.5px solid ${active ? c.primary : c.border}`,
      borderRadius: theme.radius.md,
      background: active ? c.primarySoft : c.surface,
      color: active ? c.primary : c.body,
      fontSize: '0.9rem', fontWeight: 600,
      display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '6px',
      transition: 'all .15s ease',
    }),

    submitBtn: {
      width: '100%', marginTop: '10px',
      padding: '14px 20px', borderRadius: theme.radius.md, border: 'none',
      background: theme.gradient.primary, color: '#fff',
      fontSize: '1rem', fontWeight: 700, cursor: 'pointer',
      boxShadow: theme.shadow.glow,
      display: 'inline-flex', alignItems: 'center', justifyContent: 'center', gap: '10px',
    },

    summaryCard: {
      background: c.surface, border: `1px solid ${c.border}`,
      borderRadius: theme.radius.lg, padding: '28px', position: 'sticky', top: '24px',
      boxShadow: theme.shadow.sm,
    },
    planName: { fontSize: '1.1rem', fontWeight: 800, color: c.ink, marginBottom: '4px' },
    planTag: { fontSize: '0.875rem', color: c.muted, marginBottom: '20px' },
    line: { display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '10px 0', fontSize: '0.95rem', color: c.body },
    lineDivider: { height: 1, background: c.border, margin: '8px 0' },
    totalLine: { display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', padding: '14px 0 4px', fontSize: '1.05rem', color: c.ink, fontWeight: 800 },
    totalAmt: { fontSize: '1.6rem' },

    badgeRow: { display: 'flex', flexWrap: 'wrap', gap: '8px', marginTop: '20px' },
    badge: {
      display: 'inline-flex', alignItems: 'center', gap: '6px',
      padding: '6px 10px', background: c.primarySoft, color: c.primary,
      borderRadius: theme.radius.pill, fontSize: '0.75rem', fontWeight: 600,
    },
    feature: { display: 'flex', gap: '8px', alignItems: 'flex-start', fontSize: '0.875rem', color: c.body, marginBottom: '8px', lineHeight: 1.5 },

    overlay: {
      position: 'fixed', inset: 0, background: 'rgba(15,23,42,0.6)', backdropFilter: 'blur(4px)',
      display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 9999,
    },
    overlayCard: {
      background: '#fff', padding: '40px 48px', borderRadius: theme.radius.lg, textAlign: 'center',
      boxShadow: theme.shadow.lg, maxWidth: 360,
    },
    spinner: {
      width: 44, height: 44, border: `4px solid ${c.border}`, borderTopColor: c.primary,
      borderRadius: '50%', margin: '0 auto 18px', animation: 'spin .9s linear infinite',
    },
  };

  const isMobile = typeof window !== 'undefined' && window.innerWidth < 880;
  const bodyStyle = isMobile ? { ...styles.body, ...styles.bodyMobile } : styles.body;

  return (
    <div style={styles.page}>
      <style>{`@keyframes spin{to{transform:rotate(360deg)}} input:focus{border-color:${c.primary}!important;box-shadow:0 0 0 3px rgba(79,70,229,.15)!important}`}</style>
      {/* <Navbar /> */}

      <section style={styles.hero}>
        <div style={styles.heroInner}>
          {/* <Link to="/pricing" style={styles.back}><ArrowLeft size={14} /> Back to pricing</Link> */}
          <h1 style={styles.h1}>Activate your <span style={{ background: theme.gradient.primary, WebkitBackgroundClip: 'text', backgroundClip: 'text', color: 'transparent' }}>{plan.name}</span> plan</h1>
          <p style={styles.sub}>Secure checkout · 256-bit TLS encrypted</p>
        </div>
      </section>

      <div style={bodyStyle}>
        {/* LEFT — form */}
        <form style={styles.card} onSubmit={onSubmit}>
          <h3 style={styles.sectionH}>1 · Billing details</h3>
          <div style={styles.row}>
            <div>
              <label style={styles.label}>Full name</label>
              <input style={styles.input} value={form.name} onChange={set('name')} placeholder="Rakesh Sharma" />
            </div>
            <div>
              <label style={styles.label}>Email</label>
              <input style={styles.input} type="email" value={form.email} onChange={set('email')} placeholder="you@example.com" />
            </div>
          </div>
          <div style={styles.row}>
            <div>
              <label style={styles.label}>Phone</label>
              <input style={styles.input} value={form.phone} onChange={set('phone')} placeholder="9876543210" />
            </div>
            <div>
              <label style={styles.label}>Business name (optional)</label>
              <input style={styles.input} value={form.businessName} onChange={set('businessName')} placeholder="Sharma Enterprises" />
            </div>
          </div>
          <div style={{ marginBottom: '24px' }}>
            <label style={styles.label}>GSTIN (optional, for tax invoice)</label>
            <input style={styles.input} value={form.gstin} onChange={set('gstin')} placeholder="22AAAAA0000A1Z5" />
          </div>

          <h3 style={styles.sectionH}>2 · Payment method</h3>
          <div style={styles.methodRow}>
            <div style={styles.methodTab(method === 'upi')} onClick={() => setMethod('upi')}>
              <Smartphone size={20} /> UPI
            </div>
            <div style={styles.methodTab(method === 'card')} onClick={() => setMethod('card')}>
              <CreditCard size={20} /> Card
            </div>
            <div style={styles.methodTab(method === 'netbanking')} onClick={() => setMethod('netbanking')}>
              <Building2 size={20} /> Net Banking
            </div>
          </div>

          {method === 'upi' && (
            <div style={{ marginBottom: '14px' }}>
              <label style={styles.label}>UPI ID</label>
              <input style={styles.input} value={form.upiId} onChange={set('upiId')} placeholder="yourname@okhdfcbank" />
              <div style={{ fontSize: '0.78rem', color: c.muted, marginTop: '6px' }}>You'll receive a payment request on your UPI app.</div>
            </div>
          )}
          {method === 'card' && (
            <>
              <div style={{ marginBottom: '14px' }}>
                <label style={styles.label}>Card number</label>
                <input style={styles.input} value={form.cardNumber} onChange={set('cardNumber')} placeholder="1234 5678 9012 3456" maxLength={19} />
              </div>
              <div style={{ marginBottom: '14px' }}>
                <label style={styles.label}>Name on card</label>
                <input style={styles.input} value={form.cardName} onChange={set('cardName')} placeholder="RAKESH SHARMA" />
              </div>
              <div style={styles.row}>
                <div>
                  <label style={styles.label}>Expiry (MM/YY)</label>
                  <input style={styles.input} value={form.cardExpiry} onChange={set('cardExpiry')} placeholder="08/27" maxLength={5} />
                </div>
                <div>
                  <label style={styles.label}>CVV</label>
                  <input style={styles.input} type="password" value={form.cardCvv} onChange={set('cardCvv')} placeholder="•••" maxLength={4} />
                </div>
              </div>
            </>
          )}
          {method === 'netbanking' && (
            <div style={{ marginBottom: '14px' }}>
              <label style={styles.label}>Select bank</label>
              <select style={styles.input} value={form.bank} onChange={set('bank')}>
                <option>HDFC Bank</option>
                <option>ICICI Bank</option>
                <option>State Bank of India</option>
                <option>Axis Bank</option>
                <option>Kotak Mahindra</option>
                <option>Yes Bank</option>
                <option>Punjab National Bank</option>
              </select>
            </div>
          )}

          <button type="submit" style={styles.submitBtn} disabled={submitting}>
            <Lock size={16} /> Pay ₹{total.toLocaleString('en-IN')} securely
          </button>

          <div style={{ fontSize: '0.78rem', color: c.muted, marginTop: '14px', textAlign: 'center' }}>
            By continuing you agree to our <Link to="/terms-conditions" style={{ color: c.primary }}>Terms</Link> and <Link to="/privacy-policy" style={{ color: c.primary }}>Privacy Policy</Link>.
          </div>
        </form>

        {/* RIGHT — order summary */}
        <aside>
          <div style={styles.summaryCard}>
            <div style={styles.planName}>{plan.name} Plan</div>
            <div style={styles.planTag}>{plan.tagline}</div>

            <div style={{ marginTop: '8px', marginBottom: '12px' }}>
              {plan.features.map((f, i) => (
                <div key={i} style={styles.feature}>
                  <CheckCircle2 size={16} color={c.success} style={{ flexShrink: 0, marginTop: '1px' }} /> {f}
                </div>
              ))}
            </div>

            <div style={styles.lineDivider} />

            <div style={styles.line}>
              <span>Plan amount</span>
              <span>₹{plan.amount.toLocaleString('en-IN')}</span>
            </div>
            <div style={styles.line}>
              <span>GST (18%)</span>
              <span>₹{gst.toLocaleString('en-IN')}</span>
            </div>
            <div style={styles.lineDivider} />
            <div style={styles.totalLine}>
              <span>Total payable</span>
              <span style={styles.totalAmt}>₹{total.toLocaleString('en-IN')}</span>
            </div>

            <div style={styles.badgeRow}>
              <span style={styles.badge}><ShieldCheck size={12} /> 256-bit TLS</span>
              <span style={styles.badge}><Lock size={12} /> Secure Payments</span>
              <span style={styles.badge}>RBI Compliant</span>
              <span style={styles.badge}>GST Invoice</span>
            </div>
          </div>
        </aside>
      </div>

      {submitting && (
        <div style={styles.overlay}>
          <div style={styles.overlayCard}>
            <div style={styles.spinner} />
            <h3 style={{ marginBottom: 8, color: c.ink, fontSize: '1.05rem' }}>Processing your payment…</h3>
            <p style={{ fontSize: '0.875rem', color: c.muted, margin: 0 }}>Please do not close this window.</p>
          </div>
        </div>
      )}

      {/* <Footer /> */}
    </div>
  );
};

export default Checkout;
